import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarCheck, CalendarIcon, NotebookPen, Plus, TriangleAlert, X } from "lucide-react";
import { Alert, Avatar, Button, Field, FieldLabel, IconButton, Select, Textarea, Typography } from "@akuna/ui";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { initials } from "@/lib/utils/string-utils";
import { SectionBoundary } from "./SectionBoundary";
import {
  useCreateStayIncident,
  useCreateStayObservation,
  useStayObservationTypes,
  useStayObservations,
} from "./useStayObservations";
import { useIncidentPetOptions } from "./useIncidentPetOptions";
import type { IncidentPetOption } from "./stayIncidentModel";

const petPhotoUrl = (path: string | null | undefined) =>
  path ? supabase.storage.from("pet-photos").getPublicUrl(path).data.publicUrl : undefined;

const dateAtLocalTime = (day: string) => {
  const [year, month, date] = day.split("-").map(Number);
  const now = new Date();
  return new Date(year, month - 1, date, now.getHours(), now.getMinutes(), 0, 0);
};

const formatLoggedAt = (value: string | null, language: string) => {
  if (!value) return "";
  if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = language.startsWith("pt") ? "pt-BR" : "en-US";
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
};

export function DailyObservationsSection({
  reservationId,
  petId,
  petName,
  day,
  readOnly = false,
}: {
  reservationId: string;
  petId: string;
  /** Mantido para o rótulo do campo; o pet da reserva entra sempre no incidente. */
  petName: string;
  day: string;
  readOnly?: boolean;
}) {
  const { i18n, t } = useTranslation("daycareHotel");
  const [selectedDateTime, setSelectedDateTime] = useState(() => dateAtLocalTime(day));
  const selectedDay = format(selectedDateTime, "yyyy-MM-dd");
  const selectedTime = format(selectedDateTime, "HH:mm");
  const observations = useStayObservations(reservationId, selectedDay);
  const types = useStayObservationTypes();
  const createObservation = useCreateStayObservation();
  const createIncident = useCreateStayIncident();

  const [isAdding, setIsAdding] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [participantSearch, setParticipantSearch] = useState("");
  const [selectedPet, setSelectedPet] = useState<IncidentPetOption | null>(null);
  // Só os pets: quem se envolveu já basta para o registro ser um incidente.
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const typeOptions = useMemo(
    () => (types.data ?? []).map((type) => ({ value: type.id, label: type.name })),
    [types.data],
  );

  const incidentType = (types.data ?? []).find((type) => type.id === typeId);
  const participantOptions = useIncidentPetOptions({ day: selectedDay, primaryPetId: petId, search: participantSearch });
  const participantById = useMemo(
    () => new Map((participantOptions.data ?? []).map((option) => [option.petId, option])),
    [participantOptions.data],
  );
  const availableParticipantOptions = (participantOptions.data ?? []).filter(
    (option) => !participantIds.includes(option.petId),
  );

  const resetForm = () => {
    setSelectedDateTime(dateAtLocalTime(day));
    setTypeId("");
    setDescription("");
    setParticipantSearch("");
    setSelectedPet(null);
    setParticipantIds([]);
    setIsAdding(false);
  };

  const addParticipant = (option: IncidentPetOption | null) => {
    if (!option || participantIds.includes(option.petId)) return;
    setParticipantIds((current) => [...current, option.petId]);
    setSelectedPet(null);
    setParticipantSearch("");
  };

  useEffect(() => {
    if (!typeId && typeOptions[0]) setTypeId(typeOptions[0].value);
  }, [typeId, typeOptions]);

  if (observations.isLoading || observations.isError) {
    return (
      <SectionBoundary isLoading={observations.isLoading} isError={observations.isError} skeletonClassName="h-28 rounded-2xl" />
    );
  }

  const language = i18n.resolvedLanguage ?? i18n.language ?? "pt-BR";

  const save = async () => {
    if (!typeId || !selectedTime || !description.trim()) return;
    if (incidentType?.isIncident && participantIds.length === 0) return;
    try {
      if (incidentType?.isIncident) {
        // Sem escolha de papel na tela: todo mundo entra como "envolvido".
        await createIncident.mutateAsync({
          reservationId,
          day: selectedDay,
          time: selectedTime,
          typeId,
          description: description.trim(),
          primaryPetRole: "involved",
          participants: participantIds.map((participantPetId) => ({ petId: participantPetId, role: "involved" as const })),
        });
      } else {
        await createObservation.mutateAsync({ reservationId, day: selectedDay, time: selectedTime, typeId, description: description.trim() });
      }
      toast({ title: t("panel.observationSaved") });
      resetForm();
    } catch {
      toast({ variant: "destructive", title: t("panel.observationSaveFailed") });
    }
  };

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <NotebookPen aria-hidden="true" size={18} className="text-akuna-blue" />
        <Typography variant="h5">{t("panel.sectionObservations")}</Typography>
        {(observations.data ?? []).length > 0 ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
            {(observations.data ?? []).length}
          </span>
        ) : null}
        {!readOnly && <IconButton
          className="ml-auto"
          variant="ghost"
          size="icon-sm"
          label={t("panel.addObservation")}
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </IconButton>}
      </div>

      {(observations.data ?? []).length === 0 ? (
        <Typography variant="body-sm" className="mb-2 text-muted-foreground">{t("panel.observationsEmpty")}</Typography>
      ) : (
        <ul className="mb-2 flex flex-col gap-2">
          {(observations.data ?? []).map((observation) => (
            <li
              key={observation.id}
              className={`rounded-r-lg border-l-[2px] bg-muted px-3 py-2.5 ${
                observation.isIncident ? "border-state-danger" : "border-akuna-blue"
              }`}
            >
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${
                    observation.isIncident
                      ? "border border-state-danger/30 bg-state-danger-soft text-state-danger"
                      : "border border-state-info/30 bg-state-info-soft text-state-info-foreground"
                  }`}
                >
                  {observation.isIncident ? (
                    <TriangleAlert aria-hidden="true" size={14} />
                  ) : (
                    <CalendarCheck aria-hidden="true" size={14} />
                  )}
                  <Typography variant="caption" className="font-semibold">
                    {observation.isIncident ? t("panel.incidentTag") : observation.typeName}
                  </Typography>
                </span>
                <Typography variant="caption" className="text-muted-foreground">
                  {formatLoggedAt(observation.loggedAt, language)}
                </Typography>
              </div>
              <Typography variant="body-sm" className="text-wrap break-all text-neutral-600">{observation.description}</Typography>
              {observation.isIncident && observation.participants.length > 0 ? (
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {observation.participants.map((participant) => (
                    <li key={participant.petId} className="flex items-center gap-1 rounded-full bg-card px-1.5 py-0.5 text-xs font-medium text-foreground">
                      <Avatar
                        className="h-4 w-4 shrink-0"
                        name={participant.petName}
                        src={petPhotoUrl(participant.photoPath)}
                        fallback={initials(participant.petName)}
                      />
                      {participant.petName}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {types.isError ? (
        <Alert variant="danger">{t("panel.sectionLoadError")}</Alert>
      ) : isAdding && !readOnly ? (
        <div className="grid gap-2.5 rounded-lg border border-border bg-muted p-2.5">
          <Field>
            <FieldLabel>{t("panel.observationDateTime")}</FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
                  {format(selectedDateTime, "dd/MM/yyyy 'às' HH:mm")}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="flex flex-col gap-4 p-3 sm:flex-row sm:items-start">
                  <Calendar
                    mode="single"
                    selected={selectedDateTime}
                    defaultMonth={selectedDateTime}
                    onSelect={(date) => {
                      if (!date) return;
                      setSelectedDateTime((current) => new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                        current.getHours(),
                        current.getMinutes(),
                        0,
                        0,
                      ));
                    }}
                    initialFocus
                  />
                  <div className="flex min-w-[160px] flex-col gap-2">
                    <FieldLabel htmlFor="stay-observation-time">{t("panel.observationTime")}</FieldLabel>
                    <input
                      id="stay-observation-time"
                      type="time"
                      value={selectedTime}
                      onChange={(event) => {
                        const [hours, minutes] = event.target.value.split(":").map(Number);
                        if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
                        setSelectedDateTime((current) => new Date(
                          current.getFullYear(),
                          current.getMonth(),
                          current.getDate(),
                          hours,
                          minutes,
                          0,
                          0,
                        ));
                      }}
                      className="akuna-control w-full"
                      required
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </Field>
          {typeOptions.length > 0 ? (
            <Select
              label={t("panel.observationType")}
              options={typeOptions}
              value={typeId}
              onValueChange={setTypeId}
            />
          ) : (
            <Typography variant="caption" className="text-muted-foreground">
              {t("panel.observationTypeLoading")}
            </Typography>
          )}
          <Field>
            <FieldLabel>{t("panel.observationText")}</FieldLabel>
            <Textarea
              placeholder={t("panel.observationPlaceholder")}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          {incidentType?.isIncident ? (
            <div className="grid gap-2">
              <FieldLabel>{t("panel.incidentInvolvedWith", { name: petName })}</FieldLabel>
              <Autocomplete
                id="incident-pet-search"
                ariaLabel={t("panel.incidentPetSearch")}
                value={participantSearch}
                options={availableParticipantOptions.map((option) => ({ value: option.petId, label: option.petName }))}
                onChange={(value) => {
                  setParticipantSearch(value);
                  if (!value) setSelectedPet(null);
                }}
                onSelect={(option) => addParticipant(participantById.get(option.value) ?? null)}
                selectedValue={selectedPet?.petId}
                disableLocalFilter
                popoverPortalled={false}
                useInlineDropdown
                placeholder={t("panel.incidentPetSearch")}
                emptyMessage={participantOptions.isFetching ? t("loading") : t("panel.incidentPetSearchEmpty")}
                renderOption={(option) => {
                  const pet = participantById.get(option.value);
                  return <span>{pet?.petName ?? option.label}{pet?.isPresent ? ` · ${t("panel.incidentPresentToday")}` : ""}</span>;
                }}
              />
              <div className="flex flex-wrap gap-1.5">
                {availableParticipantOptions.slice(0, 6).map((option) => (
                  <button
                    key={option.petId}
                    type="button"
                    className="rounded-full border border-border bg-card px-2.5 py-1 text-sm font-medium hover:bg-muted"
                    onClick={() => addParticipant(option)}
                  >
                    {option.petName}
                    {option.isPresent ? ` · ${t("panel.incidentPresentToday")}` : ""}
                  </button>
                ))}
              </div>
              {participantIds.length === 0 ? (
                <Typography variant="caption" className="text-state-danger">{t("panel.incidentOtherPetRequired")}</Typography>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {participantIds.map((participantPetId) => {
                    const name = participantById.get(participantPetId)?.petName ?? participantPetId;
                    return (
                      <li
                        key={participantPetId}
                        className="flex items-center gap-1 rounded-full border border-state-danger/30 bg-state-danger-soft px-2.5 py-1 text-sm font-medium text-state-danger"
                      >
                        <span className="max-w-[10rem] truncate">{name}</span>
                        <button
                          type="button"
                          aria-label={t("panel.incidentRemovePet", { name })}
                          className="rounded-full p-0.5 hover:bg-state-danger/10"
                          onClick={() => setParticipantIds((current) => current.filter((row) => row !== participantPetId))}
                        >
                          <X aria-hidden="true" size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={resetForm}>{t("cancel")}</Button>
            <Button
              disabled={!typeId || !selectedTime || !description.trim() || (incidentType?.isIncident === true && participantIds.length === 0) || createObservation.isPending || createIncident.isPending}
              loading={createObservation.isPending || createIncident.isPending}
              onClick={save}
            >
              {t("panel.observationSave")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
